import React from 'react';
import { User, Users as UsersIcon, Gift } from 'lucide-react';
import { CATEGORIAS_EGRESO } from '../../config/gastos';
import { GastoFormState } from '../../hooks/useGastoForm';
import { ComboboxLazy } from '../ui/ComboboxLazy';
import { TextInput } from '../ui/TextInput';
import { Select } from '../ui/Select';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from '../ui/tokens';
import { FormErrorAlert } from '../ui/FormErrorAlert';

type ComboboxOption = { value: string; label: string };

interface GastoFormContentProps extends GastoFormState {
  readonly onSearchMiembros: (query: string) => Promise<ComboboxOption[]>;
  readonly error: string;
  readonly onClearError: () => void;
  /** Bloque "Comprobante", arriba de todo. */
  readonly bloqueComprobante?: React.ReactNode;
  /** Medio de pago y folio, debajo del monto y la fecha. */
  readonly bloqueMedioPago?: React.ReactNode;
}

export const GastoFormContent: React.FC<GastoFormContentProps> = ({
  categoriaEgreso, setCategoriaEgreso,
  subcategoria, setSubcategoria,
  otraSubcategoria, setOtraSubcategoria,
  monto, setMonto,
  fecha, setFecha,
  descripcion, setDescripcion,
  requiereReembolso, setRequiereReembolso,
  reembolsoTipo, setReembolsoTipo,
  miembroReembolsoId, setMiembroReembolsoId,
  nombreExternoReembolso, setNombreExternoReembolso,
  esDonacion,
  donanteTipo, setDonanteTipo,
  miembroDonante, setMiembroDonante,
  nombreExternoDonante, setNombreExternoDonante,
  subcategorias,
  reembolsoClassName,
  pillClasses,
  handleDonacionToggle,
  error,
  onClearError,
  onSearchMiembros,
  bloqueComprobante,
  bloqueMedioPago,
}) => (
  <>
    <FormErrorAlert error={error} onClear={onClearError} />

    {bloqueComprobante}

    <div>
      <p id="categoria-gasto-label" className="block text-sm font-semibold text-slate-700 mb-3">
        Categoría de Gasto *
      </p>
      <div aria-labelledby="categoria-gasto-label" className="grid grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => setCategoriaEgreso('general')}
          className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${categoriaEgreso === 'general' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
        >
          <span className="font-medium">{CATEGORIAS_EGRESO.general}</span>
        </button>
        <button
          type="button"
          onClick={() => setCategoriaEgreso('liturgico')}
          className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${categoriaEgreso === 'liturgico' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
        >
          <span className="font-medium">{CATEGORIAS_EGRESO.liturgico}</span>
        </button>
        <button
          type="button"
          onClick={() => setCategoriaEgreso('misional')}
          className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${categoriaEgreso === 'misional' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
        >
          <span className="font-medium">{CATEGORIAS_EGRESO.misional}</span>
        </button>
      </div>
    </div>

    <Select
      label="Subcategoría"
      options={subcategorias.map((sub) => ({ value: sub, label: sub }))}
      value={subcategoria}
      onChange={setSubcategoria}
      placeholder="Selecciona una subcategoría"
      required
    />

    {subcategoria === 'Otra' && (
      <TextInput
        id="otraSubcategoria"
        label="Especifica la Subcategoría"
        value={otraSubcategoria}
        onChange={setOtraSubcategoria}
        placeholder="Ej: Material de oficina, Decoración, etc."
        required
      />
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <TextInput
          id="monto"
          label="Monto"
          type="number"
          value={monto}
          onChange={setMonto}
          placeholder="0"
          min={0}
          step={1}
          prefixIcon={<span className="text-slate-500 font-medium text-sm">$</span>}
          required
        />
      </div>
      <div>
        <label htmlFor="fecha" className="block text-sm font-medium text-slate-600 mb-1.5">
          Fecha *
        </label>
        <input
          type="date"
          id="fecha"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
          required
        />
      </div>
    </div>

    {bloqueMedioPago}

    <TextInput
      id="descripcion"
      label="Descripción"
      value={descripcion}
      onChange={setDescripcion}
      placeholder="Describe el gasto realizado..."
      required
    />

    <div className="border-t border-slate-200 pt-6">
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleDonacionToggle}
          className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${pillClasses.donacionPill}`}
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${pillClasses.donacionCheck}`}>
            {esDonacion && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Gift className={`w-4 h-4 flex-shrink-0 ${pillClasses.donacionGift}`} />
            <span className={`text-sm font-semibold ${pillClasses.donacionText}`}>Es una donación</span>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { if (!esDonacion) setRequiereReembolso(!requiereReembolso); }}
          className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${reembolsoClassName}`}
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${pillClasses.reembolsoCheck}`}>
            {requiereReembolso && !esDonacion && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className={`text-sm font-semibold ${pillClasses.reembolsoText}`}>
            Pendiente de reembolso
          </span>
        </button>
      </div>
    </div>

    {esDonacion && (
      <div className="space-y-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
        <div>
          <p id="donante-label" className="block text-sm font-semibold text-slate-700 mb-3">
            ¿Quién hizo la donación? <span className="text-slate-400 font-normal">(opcional)</span>
          </p>
          <div aria-labelledby="donante-label" className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setDonanteTipo('miembro')}
              className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${donanteTipo === 'miembro' ? 'border-emerald-500 bg-white text-emerald-700' : 'border-emerald-200 hover:border-emerald-300 text-slate-600 bg-white'}`}
            >
              <UsersIcon className="w-5 h-5" />
              <span className="font-medium">Miembro</span>
            </button>
            <button
              type="button"
              onClick={() => setDonanteTipo('externo')}
              className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${donanteTipo === 'externo' ? 'border-emerald-500 bg-white text-emerald-700' : 'border-emerald-200 hover:border-emerald-300 text-slate-600 bg-white'}`}
            >
              <User className="w-5 h-5" />
              <span className="font-medium">Otra Persona</span>
            </button>
          </div>
        </div>
        {donanteTipo === 'miembro' ? (
          <ComboboxLazy
            label="Seleccionar Miembro Donante"
            value={miembroDonante}
            onChange={setMiembroDonante}
            onSearch={onSearchMiembros}
            placeholder="Buscar miembro por nombre o RUT..."
            emptyMessage="No se encontraron miembros"
            allowClear
          />
        ) : (
          <TextInput
            id="nombreExternoDonante"
            label="Nombre del Donante"
            value={nombreExternoDonante}
            onChange={setNombreExternoDonante}
            placeholder="Nombre completo del donante"
          />
        )}
      </div>
    )}

    {requiereReembolso && (
      <div className="space-y-4 bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
        <div>
          <p id="reembolsar-a-label" className="block text-sm font-semibold text-slate-700 mb-3">
            ¿A quién se debe reembolsar? *
          </p>
          <div aria-labelledby="reembolsar-a-label" className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setReembolsoTipo('miembro')}
              className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${reembolsoTipo === 'miembro' ? 'border-orange-500 bg-white text-orange-700' : 'border-orange-200 hover:border-orange-300 text-slate-600 bg-white'}`}
            >
              <UsersIcon className="w-5 h-5" />
              <span className="font-medium">Miembro</span>
            </button>
            <button
              type="button"
              onClick={() => setReembolsoTipo('externo')}
              className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${reembolsoTipo === 'externo' ? 'border-orange-500 bg-white text-orange-700' : 'border-orange-200 hover:border-orange-300 text-slate-600 bg-white'}`}
            >
              <User className="w-5 h-5" />
              <span className="font-medium">Otra Persona</span>
            </button>
          </div>
        </div>
        {reembolsoTipo === 'miembro' ? (
          <ComboboxLazy
            label="Seleccionar Miembro para Reembolso"
            value={miembroReembolsoId}
            onChange={setMiembroReembolsoId}
            onSearch={onSearchMiembros}
            placeholder="Buscar miembro por nombre o RUT..."
            emptyMessage="No se encontraron miembros"
            required
            allowClear
          />
        ) : (
          <TextInput
            id="nombreExternoReembolso"
            label="Nombre de la Persona"
            value={nombreExternoReembolso}
            onChange={setNombreExternoReembolso}
            placeholder="Nombre completo de la persona a reembolsar"
            required
          />
        )}
      </div>
    )}
  </>
);
